'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { api } from '../api/client';
import type { User } from '../api/types';

export function isUserOnboarded(user: User | null): boolean {
  if (!user) return false;
  return user.onboarding_completed && user.tos_acceptance_version === user.current_tos_version;
}

export function isUserEligible(user: User | null): boolean {
  if (!user) return true;
  return user.eligible === true;
}

type UserContextType = {
  user: User | null;
  setUser: (user: User | null) => void;
  hydrated: boolean;
};

const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  hydrated: false,
});

export function UserProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from /api/me on mount (session cookie is sent automatically)
  useEffect(() => {
    api
      .getMe()
      .then((me) => {
        setUser(me);
      })
      .catch(() => {
        // 401 or network error — user is not logged in
        setUser(null);
      })
      .finally(() => {
        setHydrated(true);
      });
  }, []);

  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => {
      window.removeEventListener('auth:expired', handleExpired);
    };
  }, []);

  useEffect(() => {
    const handleReaccept = () => {
      api
        .getMe()
        .then((me) => setUser(me))
        .catch(() => setUser(null));
    };
    window.addEventListener('tos:reaccept', handleReaccept);
    return () => {
      window.removeEventListener('tos:reaccept', handleReaccept);
    };
  }, []);

  // Re-check session when the tab becomes visible again (e.g. user returns
  // after the tab was backgrounded for hours and the JWT expired).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && hydrated) {
        api
          .getMe()
          .then((me) => {
            setUser(me);
          })
          .catch(() => {
            setUser(null);
          });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hydrated]);

  return (
    <UserContext.Provider value={{ user, setUser, hydrated }}>{children}</UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
